import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import FleetPage from "@/pages/_designer_fleet/ui/FleetPage"; // designer's agate presentation, wired to live /api/fleet
import GraphExplorer from "@/pages/GraphExplorer";
import ExploreGraph from "@/pages/ExploreGraph";
import CostManagement from "@/pages/CostManagement";
import Policy from "@/pages/Policy";
import ToolFilter from "@/pages/ToolFilter";
import Identity from "@/pages/Identity";
import Audit from "@/pages/Audit";
import Network from "@/pages/Network";
import Settings from "@/pages/Settings";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Connect from "@/pages/Connect";
import ChangePassword from "@/pages/ChangePassword";
import Operators from "@/pages/Operators";
import Users from "@/pages/Users";
import Governance from "@/pages/Governance";
import AgenticScan from "@/pages/AgenticScan";
import McpScanReportPreview from "@/pages/McpScanReportPreview";
import Integrations from "@/pages/Integrations";
import { useAuth } from "@/lib/auth/AuthProvider";

export default function App() {
  // MCP Scan is a runtime-flagged surface (server whoami → mcpScanEnabled): only
  // rc38, the host holding the trust-scan key, ships it. When off, the route
  // redirects so a bookmarked /mcp-scan doesn't dead-end on a blank Layout.
  const { mcpScanEnabled } = useAuth();
  return (
    <Routes>
      <Route path="/callback" element={<AuthCallback />} />
      {/* DEV-ONLY: MCP Scan report harness against the checked-in fixture. No auth
          so it previews without a login; not linked in the nav. Remove once the
          live /scans passthrough drives the report. */}
      {import.meta.env.DEV && <Route path="/dev/scan-report" element={<McpScanReportPreview />} />}
      <Route path="/login" element={<Login />} />
      <Route path="/connect" element={<Connect />} />
      <Route element={<RequireAuth />}>
        <Route path="/password" element={<ChangePassword />} />
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/fleet" replace />} />
          {/* Overview removed 2026-09-03 — Host Fleet is the console's home page now. */}
          <Route path="/overview" element={<Navigate to="/fleet" replace />} />
          <Route path="/dashboard" element={<Navigate to="/fleet" replace />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/agent-activity" element={<GraphExplorer />} />
          {/* Full CCPD Agate explorer embedded in-console (nav item under Policy). */}
          <Route path="/explore" element={<ExploreGraph />} />
          {/* CCPD LLM Cost Management dashboard, embedded in-console. */}
          <Route path="/cost" element={<CostManagement />} />
          <Route path="/graph" element={<Navigate to="/explore" replace />} />
          {/* Sandboxes deprecated 2026-08-21 → redirect to Fleet (SSO) */}
          <Route path="/sandboxes" element={<Navigate to="/fleet" replace />} />
          <Route path="/sandboxes/fleet" element={<Navigate to="/fleet" replace />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/tool-filter" element={<ToolFilter />} />
          <Route path="/identity" element={<Identity />} />
          <Route path="/network" element={<Network />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/settings" element={<Settings />} />
          {/* Admin-only in the API (rbac.go gates /api/v1/users*). RequireAdmin
              keeps a read-only operator off a page that would only 403. */}
          <Route element={<RequireAdmin />}>
            <Route path="/operators" element={<Operators />} />
            {/* Fleet users — admin-gated in the API. */}
            <Route path="/users" element={<Users />} />
            {/* MCP Scan retired 2026-09-16 → its bookmark lands on Agentic Scan. */}
            <Route path="/mcp-scan" element={<Navigate to="/agentic-scan" replace />} />
            {/* Agentic Scan — the scan surface (runtime flag + admin gate). */}
            <Route
              path="/agentic-scan"
              element={mcpScanEnabled ? <AgenticScan /> : <Navigate to="/overview" replace />}
            />
            <Route path="/governance" element={<Governance />} />
            <Route path="/integrations" element={<Integrations />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

// RequireAuth — gate every authed route. No token → redirect to /login,
// remembering where the user wanted to go so we can return them there
// after sign-in.
//
// REDESIGN PROTOTYPE: the gate is disabled — there is no backend to log
// into, so the login page is dead weight while iterating on the UI.
// Restore the commented block before this branch goes anywhere real.
function RequireAuth() {
  // Auth gate LIVE: an unauthenticated visit redirects to /login (remembering
  // where the user was headed) instead of landing on a page that just 401s with
  // "missing bearer token". The console has a real static-login backend now.
  const { token, mustChangePassword, identityLoading } = useAuth();
  const location = useLocation();
  // Local dev (vite) has no real login backend — the mock only persists policy
  // edits — so the gate would trap you at /login. Bypass in dev; enforce in the
  // production build served by the console.
  if (!token && !import.meta.env.DEV) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  // Forced rotation. The SERVER enforces this — a `pwreset` identity is refused
  // everywhere except the change itself — so without this redirect the user
  // would see a console of 403s and no way to act. Wait for identity to resolve
  // first; redirecting on a half-known identity bounces people mid-refresh.
  if (
    token &&
    !identityLoading &&
    mustChangePassword &&
    location.pathname !== "/password"
  ) {
    return <Navigate to="/password" replace />;
  }
  return <Outlet />;
}

// RequireAdmin — UI-side gate for admin-only pages. NOT a security control: the
// API refuses these routes for a viewer regardless. This exists so a read-only
// operator gets a redirect instead of a page that renders and then fails.
function RequireAdmin() {
  const { role, identityLoading } = useAuth();
  if (identityLoading) return null;
  if (role !== "admin") return <Navigate to="/fleet" replace />;
  return <Outlet />;
}
